import React, { useState, useEffect, useRef } from 'react';
import { Clock, ChevronLeft, ChevronRight, TrendingUp, Sparkles, ShoppingBag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import JourneyHeader from './JourneyHeader';
import Footer from './Footer';

const InspiyahJourney = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('latest');
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('All Stories');

  // Blog post data
  const blogPosts = [
    {
      id: 1,
      title: 'The 1010 Portal: Opening Doors to Divine Alignment',
      excerpt: 'Discover how the sacred 1010 portal can transform your spiritual journey...',
      category: 'Spirituality',
      image: 'https://images.unsplash.com/photo-1528715471579-d1bcf0ba5e83?w=800&h=600&fit=crop',
      author: 'Keeper Of The Fiyah',
      date: 'January 20, 2025',
      readTime: '5 min read',
      slug: '1010-portal-divine-alignment',
      hasContent: true
    },
    {
      id: 2,
      title: 'YAH HEALTH WEALTH HAPPINESS: The Universal Pillars',
      excerpt: 'Understanding the three sacred pillars that form the foundation...',
      category: 'Wellness',
      image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800&h=600&fit=crop',
      author: 'FLY FREQUENCY',
      date: 'January 18, 2025',
      readTime: '7 min read'
    },
    {
      id: 3,
      title: 'Building Your Business with A.R.T. ENERGI',
      excerpt: 'How Authenticity, Reliability, and Truth create sustainable success...',
      category: 'Business',
      image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&h=600&fit=crop',
      author: 'C to the T',
      date: 'January 15, 2025',
      readTime: '6 min read'
    },
    {
      id: 4,
      title: 'Sacred Streetwear: Fashion as Spiritual Expression',
      excerpt: 'How INSPIYAH CULTURE bridges fashion and spiritual consciousness...',
      category: 'Culture',
      image: 'https://images.unsplash.com/photo-1529374255404-311a2a4f1fd9?w=800&h=600&fit=crop',
      author: 'Keeper Of The Fiyah',
      date: 'January 12, 2025',
      readTime: '4 min read'
    },
    {
      id: 5,
      title: 'Digital Transformation for the Soul',
      excerpt: 'Leveraging technology to amplify your spiritual message...',
      category: 'Technology',
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&h=600&fit=crop',
      author: 'C to the T',
      date: 'January 10, 2025',
      readTime: '8 min read'
    },
    {
      id: 6,
      title: 'The Power of 888: Numerology and Abundance',
      excerpt: 'Unlock the secrets of angel number 888 and infinite abundance...',
      category: 'Spirituality',
      image: 'https://images.unsplash.com/photo-1518531933037-91b2f5f229cc?w=800&h=600&fit=crop',
      author: 'FLY FREQUENCY',
      date: 'January 8, 2025',
      readTime: '5 min read'
    },
    {
      id: 7,
      title: 'Faith.Love.Yah: The FLY Philosophy',
      excerpt: 'Exploring the three pillars that guide every decision...',
      category: 'Spirituality',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&h=600&fit=crop',
      author: 'FLY FREQUENCY',
      date: 'January 5, 2025',
      readTime: '6 min read'
    },
    {
      id: 8,
      title: 'Building Community Through Conscious Commerce',
      excerpt: 'How purposeful products create meaningful connections...',
      category: 'Business',
      image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop',
      author: 'C to the T',
      date: 'January 3, 2025',
      readTime: '5 min read'
    }
  ];

  const carouselPosts = blogPosts.slice(0, 3);

  // Category filter for blog posts
  const categories = ['All Stories', 'Spirituality', 'Business', 'Wellness', 'Culture', 'Technology'];

  // Submenu items for specific categories
  const categorySubmenus = {
    'Spirituality': ['YHWH יהוה', 'Spiritual Foundation', 'Faith'],
    'Wellness': ['Health', 'Wealth', 'Happiness'],
    'Culture': ['Yah', 'Health', 'Wealth', 'Happiness', 'Art Energi', 'Inspiyah Culture']
  };

  const [openSubmenu, setOpenSubmenu] = useState(null);
  const submenuRef = useRef(null);

  // Close submenu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (submenuRef.current && !submenuRef.current.contains(event.target)) {
        setOpenSubmenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Category Navigation Bar (Below Header)
  const CategoryNav = () => (
    <section className="sticky top-24 z-40 py-4" style={{ background: 'linear-gradient(to right, #403B4A 0%, #E7E9BB 51%, #403B4A 100%)' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between">
          {/* Category Pills */}
          <div ref={submenuRef} className="flex flex-wrap gap-3 relative">
            {categories.map((category) => (
              <div key={category} className="relative">
                <button
                  onClick={() => {
                    setSelectedCategory(category);
                    // Toggle submenu for categories that have one
                    if (categorySubmenus[category]) {
                      setOpenSubmenu(openSubmenu === category ? null : category);
                    } else {
                      setOpenSubmenu(null);
                    }
                  }}
                  className={`px-6 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                    selectedCategory === category
                      ? "bg-gradient-to-r from-brand-deep to-fire-main text-white shadow-lg scale-105"
                      : "bg-white/80 text-gray-700 hover:bg-white hover:shadow-md border border-gray-200"
                  }`}
                >
                  {category}
                  {categorySubmenus[category] && (
                    <span className="ml-1 text-xs">▼</span>
                  )}
                </button>

                {/* Submenu Dropdown */}
                {openSubmenu === category && categorySubmenus[category] && (
                  <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-2xl border-2 border-brand-gold py-2 min-w-[200px] z-50" style={{ boxShadow: '0 0 25px rgba(212, 175, 55, 0.3)' }}>
                    {categorySubmenus[category].map((item, index) => (
                      <button
                        key={index}
                        onClick={(e) => {
                          e.stopPropagation();
                          // Handle submenu item click
                          console.log(`Selected: ${category} > ${item}`);
                          setOpenSubmenu(null);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-brand-deep hover:to-fire-main hover:text-white transition-all duration-200"
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Quick Links */}
          <div className="hidden lg:flex items-center space-x-4 text-sm">
            <a href="#featured" className="text-gray-700 hover:text-brand-deep font-medium transition-colors">
              Featured
            </a>
            <span className="text-gray-400">|</span>
            <a href="#latest" className="text-gray-700 hover:text-brand-deep font-medium transition-colors">
              Latest
            </a>
            <span className="text-gray-400">|</span>
            <a href="#popular" className="text-gray-700 hover:text-brand-deep font-medium transition-colors">
              Popular
            </a>
          </div>
        </div>
      </div>
    </section>
  );

  // Hero Section
  const HeroSection = () => (
    <section className="pt-32 pb-16" style={{ background: 'linear-gradient(to right, #403B4A 0%, #E7E9BB 51%, #403B4A 100%)' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-12">
          <h1 className="text-6xl md:text-7xl font-bold bg-gradient-to-r from-brand-deep via-fire-main to-sacred-teal bg-clip-text text-transparent mb-4">
            INSPIYAH JOURNEY
          </h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Uplift. Enlighten. Empower. Stories of transformation, wisdom, and purpose.
          </p>
        </div>

        {/* 3-Column Layout */}
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Column 1: Square Carousel */}
          <div className="lg:col-span-4">
            <div 
              onClick={() => carouselPosts[carouselIndex].hasContent && navigate(`/article/${carouselPosts[carouselIndex].slug}`)}
              className={`relative h-[500px] rounded-3xl overflow-hidden shadow-xl group border-2 border-brand-gold ${carouselPosts[carouselIndex].hasContent ? 'cursor-pointer' : ''}`} 
              style={{ boxShadow: '0 0 30px rgba(212, 175, 55, 0.3)' }}
            >
              <img 
                src={carouselPosts[carouselIndex].image}
                alt={carouselPosts[carouselIndex].title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
              
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <span className="inline-block px-4 py-1 bg-fire-main rounded-full text-sm font-semibold mb-3">
                  {carouselPosts[carouselIndex].category}
                </span>
                <h2 className="text-2xl font-bold mb-2">
                  {carouselPosts[carouselIndex].title}
                </h2>
                <p className="text-white/80 text-sm mb-3">{carouselPosts[carouselIndex].excerpt}</p>
                <div className="flex items-center justify-between text-xs text-white/70">
                  <span>{carouselPosts[carouselIndex].date}</span>
                  <div className="flex items-center space-x-1">
                    <Clock size={14} />
                    <span>{carouselPosts[carouselIndex].readTime}</span>
                  </div>
                </div>
              </div>

              {/* Carousel Arrows */}
              <button 
                onClick={() => setCarouselIndex((carouselIndex - 1 + carouselPosts.length) % carouselPosts.length)}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/40 transition-colors"
              >
                <ChevronLeft className="text-white" size={24} />
              </button>
              <button 
                onClick={() => setCarouselIndex((carouselIndex + 1) % carouselPosts.length)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/40 transition-colors"
              >
                <ChevronRight className="text-white" size={24} />
              </button>

              {/* Carousel Indicators */}
              <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 flex space-x-2">
                {carouselPosts.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCarouselIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === carouselIndex ? 'bg-white w-8' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Column 2: Vertical Rectangle with 2 Stories */}
          <div className="lg:col-span-4 space-y-4">
            {blogPosts.slice(3, 5).map((post) => (
              <div 
                key={post.id} 
                className="group cursor-pointer"
                onClick={() => post.hasContent && navigate(`/article/${post.slug}`)}
              >
                <div className="relative h-[242px] rounded-2xl overflow-hidden shadow-lg border-2 border-brand-gold" style={{ boxShadow: '0 0 25px rgba(212, 175, 55, 0.25)' }}>
                  <img 
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                  
                  <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                    <span className="inline-block px-3 py-1 bg-sacred-teal rounded-full text-xs font-semibold mb-2">
                      {post.category}
                    </span>
                    <h3 className="text-lg font-bold mb-2 group-hover:text-brand-gold transition-colors">
                      {post.title}
                    </h3>
                    <div className="flex items-center justify-between text-xs text-white/70">
                      <span>{post.date}</span>
                      <div className="flex items-center space-x-1">
                        <Clock size={12} />
                        <span>{post.readTime}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Column 3: Vertical Rectangle with Tabs (Latest/Popular) - 4 stories each */}
          <div className="lg:col-span-4">
            <div className="bg-white rounded-3xl shadow-lg p-6 h-[500px] overflow-hidden border-2 border-brand-gold" style={{ boxShadow: '0 0 30px rgba(212, 175, 55, 0.3)' }}>
              {/* Tabs */}
              <div className="flex space-x-4 mb-6 border-b border-gray-200 pb-3">
                <button
                  onClick={() => setActiveTab('latest')}
                  className={`pb-2 text-sm font-semibold transition-all relative ${
                    activeTab === 'latest' ? 'text-brand-deep' : 'text-gray-500'
                  }`}
                >
                  <Sparkles size={16} className="inline mr-1" />
                  Latest
                  {activeTab === 'latest' && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-deep"></div>
                  )}
                </button>
                <button
                  onClick={() => setActiveTab('popular')}
                  className={`pb-2 text-sm font-semibold transition-all relative ${
                    activeTab === 'popular' ? 'text-brand-deep' : 'text-gray-500'
                  }`}
                >
                  <TrendingUp size={16} className="inline mr-1" />
                  Popular
                  {activeTab === 'popular' && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-deep"></div>
                  )}
                </button>
              </div>

              {/* 4 Stories */}
              <div className="space-y-4 overflow-y-auto h-[400px] pr-2">
                {blogPosts.slice(0, 4).map((post) => (
                  <div key={post.id} className="group cursor-pointer flex space-x-3 pb-4 border-b border-gray-100 last:border-0">
                    <div className="relative w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                      <img 
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="inline-block px-2 py-0.5 bg-brand-deep/10 text-brand-deep rounded text-xs font-semibold mb-1">
                        {post.category}
                      </span>
                      <h4 className="text-sm font-bold text-gray-900 line-clamp-2 group-hover:text-fire-main transition-colors mb-1">
                        {post.title}
                      </h4>
                      <div className="flex items-center text-xs text-gray-500">
                        <span>{post.date}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  // Shop CTA Banner
  const ShopBanner = () => (
    <section className="py-16 bg-gradient-to-r from-brand-deep via-fire-main to-sacred-teal">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col md:flex-row items-center justify-between text-white">
          <div className="mb-6 md:mb-0">
            <div className="flex items-center space-x-3 mb-2">
              <ShoppingBag size={32} />
              <h2 className="text-4xl font-bold">INSPIYAH CULTURE</h2>
            </div>
            <p className="text-xl text-white/90">Spiritual streetwear for the conscious creator</p>
          </div>
          <a 
            href="/store"
            className="px-8 py-4 bg-white text-brand-deep font-bold rounded-full hover:bg-brand-gold hover:text-white transition-all duration-300 hover:shadow-2xl transform hover:scale-105"
          >
            Shop Now
          </a>
        </div>
      </div>
    </section>
  );

  // Featured Stories - 4 Square Containers
  const FeaturedStories = () => (
    <section className="py-16" style={{ background: 'linear-gradient(to left, #403B4A 0%, #E7E9BB 51%, #403B4A 100%)' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <h2 className="text-4xl font-bold text-center mb-12 bg-gradient-to-r from-brand-deep via-fire-main to-sacred-teal bg-clip-text text-transparent">
          Featured Stories
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {blogPosts.slice(0, 4).map((post) => (
            <article 
              key={post.id} 
              onClick={() => post.hasContent && navigate(`/article/${post.slug}`)}
              className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-2 border-brand-gold" 
              style={{ boxShadow: '0 0 25px rgba(212, 175, 55, 0.25)' }}
            >
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-brand-deep rounded-full text-xs font-bold">
                    {post.category}
                  </span>
                </div>
              </div>
              
              <div className="p-5">
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-fire-main transition-colors line-clamp-2">
                  {post.title}
                </h3>
                <p className="text-gray-600 text-sm mb-3 line-clamp-2">{post.excerpt}</p>
                
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <div className="text-xs text-gray-500">
                    <div className="font-semibold text-gray-700">{post.author}</div>
                  </div>
                  <div className="flex items-center space-x-1 text-xs text-gray-500">
                    <Clock size={12} />
                    <span>{post.readTime}</span>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );

  // Articles + Product Recommendations - 2 Column Layout (2/3 + 1/3)
  const ArticlesAndProducts = () => {
    const products = [
      {
        id: 1,
        name: 'Sacred Frankincense Oil',
        description: 'Ancient oil for meditation and spiritual awakening',
        image: 'https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?w=400&h=400&fit=crop',
        price: '$29.99',
        category: 'Essential Oils'
      },
      {
        id: 2,
        name: 'Sage Smudge Bundle',
        description: 'White sage for cleansing and purification',
        image: 'https://images.unsplash.com/photo-1602524206684-84a49bc4c6d7?w=400&h=400&fit=crop',
        price: '$14.99',
        category: 'Herbs'
      },
      {
        id: 3,
        name: 'Lavender Essential Oil',
        description: 'Calming oil for peace and relaxation',
        image: 'https://images.unsplash.com/photo-1611080626919-7cf5a9dbab5b?w=400&h=400&fit=crop',
        price: '$24.99',
        category: 'Essential Oils'
      },
      {
        id: 4,
        name: 'Palo Santo Sticks',
        description: 'Sacred wood for energy clearing',
        image: 'https://images.unsplash.com/photo-1590736969955-71cc94901144?w=400&h=400&fit=crop',
        price: '$18.99',
        category: 'Herbs'
      },
      {
        id: 5,
        name: 'Rose Quartz Crystal Oil',
        description: 'Infused oil for self-love and healing',
        image: 'https://images.unsplash.com/photo-1615529182904-14819c35db37?w=400&h=400&fit=crop',
        price: '$32.99',
        category: 'Essential Oils'
      }
    ];

    return (
      <section className="py-16" style={{ background: 'linear-gradient(to right, #403B4A 0%, #E7E9BB 51%, #403B4A 100%)' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column: Articles (2/3 width) */}
            <div className="lg:col-span-2">
              <h2 className="text-4xl font-bold mb-8 bg-gradient-to-r from-brand-deep via-fire-main to-sacred-teal bg-clip-text text-transparent">
                Latest Articles
              </h2>
              <div className="space-y-6">
                {blogPosts.slice(0, 7).map((post) => (
                  <article 
                    key={post.id} 
                    onClick={() => post.hasContent && navigate(`/article/${post.slug}`)}
                    className="group cursor-pointer bg-gray-50 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 flex border-2 border-brand-gold" 
                    style={{ boxShadow: '0 0 20px rgba(212, 175, 55, 0.2)' }}
                  >
                    <div className="relative w-48 h-48 flex-shrink-0 overflow-hidden">
                      <img 
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                      />
                    </div>
                    
                    <div className="p-6 flex-1">
                      <span className="inline-block px-3 py-1 bg-brand-deep/10 text-brand-deep rounded-full text-xs font-bold mb-3">
                        {post.category}
                      </span>
                      <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-fire-main transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">{post.excerpt}</p>
                      
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <div className="flex items-center space-x-3">
                          <span className="font-semibold text-gray-700">{post.author}</span>
                          <span>•</span>
                          <span>{post.date}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Clock size={14} />
                          <span>{post.readTime}</span>
                        </div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>

            {/* Right Column: Product Recommendations (1/3 width) */}
            <div className="lg:col-span-1">
              <div className="sticky top-32">
                <h3 className="text-2xl font-bold mb-6 bg-gradient-to-r from-brand-deep to-fire-main bg-clip-text text-transparent">
                  Recommended Products
                </h3>
                <div className="space-y-4">
                  {products.map((product) => (
                    <div key={product.id} className="group cursor-pointer bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-2 border-brand-gold" style={{ boxShadow: '0 0 20px rgba(212, 175, 55, 0.2)' }}>
                      <div className="relative h-40 overflow-hidden">
                        <img 
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                        />
                        <div className="absolute top-3 left-3">
                          <span className="px-2 py-1 bg-black/70 text-white rounded text-xs font-semibold">
                            {product.category}
                          </span>
                        </div>
                      </div>
                      <div className="p-4">
                        <h4 className="font-bold text-gray-900 mb-1 group-hover:text-fire-main transition-colors">
                          {product.name}
                        </h4>
                        <p className="text-gray-600 text-xs mb-3 line-clamp-2">{product.description}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-lg font-bold text-brand-deep">{product.price}</span>
                          <button className="px-4 py-2 bg-gradient-to-r from-brand-deep to-fire-main text-white text-xs font-semibold rounded-full hover:shadow-lg transition-all duration-300">
                            Shop Now
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* CTA Card */}
                <div className="mt-6 bg-gradient-to-br from-brand-deep to-fire-main rounded-xl p-6 text-white text-center border-2 border-brand-gold" style={{ boxShadow: '0 0 25px rgba(212, 175, 55, 0.3)' }}>
                  <ShoppingBag size={32} className="mx-auto mb-3" />
                  <h4 className="font-bold text-lg mb-2">Explore More</h4>
                  <p className="text-white/90 text-sm mb-4">Discover our full collection of spiritual essentials</p>
                  <a 
                    href="/store" 
                    className="inline-block px-6 py-3 bg-white text-brand-deep font-semibold rounded-full hover:bg-brand-gold hover:text-white transition-all duration-300"
                  >
                    Visit Store
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(to bottom, #403B4A 0%, #E7E9BB 50%, #403B4A 100%)' }}>
      <JourneyHeader activeSection="journey" />
      <CategoryNav />
      
      <HeroSection />
      <ShopBanner />
      <FeaturedStories />
      <ArticlesAndProducts />
      
      <Footer />
    </div>
  );
};

export default InspiyahJourney;